import { useSession } from '../api/session';
import type { TimelinePayload } from '../api/types';
import type { DayPhase } from '../lib/ui';
import { m2t } from '../lib/ui';
import { useUi, type FilterChip } from '../state/ui';
import { filterCounts, planningOn } from './derive';
import './toolbar.css';

const FILTER_DEFS: { key: FilterChip; label: string; dot: string }[] = [
  { key: 'nujni', label: 'nujni & nepotrjeni', dot: 'oklch(0.58 0.13 25)' },
  { key: 'vrzeli', label: 'vrzeli', dot: 'oklch(0.62 0.11 45)' },
  { key: 'caka', label: 'čaka potrditev', dot: 'oklch(0.68 0.11 75)' },
  { key: 'brifing', label: 'brez brifinga', dot: 'oklch(0.6 0.06 245)' },
];

export function Toolbar({
  timeline,
  phase,
  live,
  silent,
  now,
  lensOn = false,
}: {
  timeline: TimelinePayload | undefined;
  phase: DayPhase;
  live: boolean;
  silent: boolean;
  now: number;
  /** The board is looking through somebody, so hovering traces nobody. */
  lensOn?: boolean;
}) {
  const {
    boardView,
    setBoardView,
    filters,
    toggleFilter,
    setSilentOverride,
    setLiveOverride,
    setAsideTab,
    pingNow,
    sectorFocus,
    setSectorFocus,
    hoveredVolunteerId,
    setHoveredVolunteer,
  } = useUi();

  const planning = planningOn(live, phase);
  const { can } = useSession();
  // "brez brifinga" filters by send status (SPEC §1.1L).
  const chips = FILTER_DEFS.filter((f) => f.key !== 'brifing' || can('comms:read'));
  const counts = timeline && planning ? filterCounts(timeline) : null;
  const hoveredName = lensOn ? undefined : timeline?.volunteers.find((v) => v.id === hoveredVolunteerId)?.name;

  return (
    <div className="tb">
      <div className="tb-seg">
        <button
          type="button"
          className={`tb-seg-btn${!live ? ' tb-seg-btn--active' : ''}`}
          onClick={() => {
            setLiveOverride(phase === 'live' ? false : null);
            setAsideTab('opozorila');
          }}
        >
          Načrtovanje
        </button>
        <button
          type="button"
          className={`tb-seg-btn${live ? ' tb-seg-btn--active tb-seg-btn--live' : ''}`}
          onClick={() => {
            setLiveOverride(true);
            setAsideTab('zivo');
            setTimeout(() => pingNow(), 80);
          }}
        >
          V živo
        </button>
      </div>

      <div className="tb-seg">
        <button
          type="button"
          className={`tb-seg-btn${boardView === 'mesta' ? ' tb-seg-btn--active' : ''}`}
          onClick={() => setBoardView('mesta')}
        >
          Po mestih
        </button>
        <button
          type="button"
          className={`tb-seg-btn${boardView === 'ljudje' ? ' tb-seg-btn--active' : ''}`}
          onClick={() => setBoardView('ljudje')}
        >
          Po ljudeh
        </button>
      </div>

      <div className="tb-divider" />

      {planning ? (
        <>
          <div className="tb-chips">
            {chips.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`tb-chip${filters.has(f.key) ? ' tb-chip--on' : ''}`}
                onClick={() => toggleFilter(f.key)}
              >
                <span className="tb-chip-dot" style={{ background: f.dot }} />
                {f.label}
                {counts ? <span className="tb-chip-count">{counts[f.key]}</span> : null}
              </button>
            ))}
          </div>
          <div className="tb-divider" />
        </>
      ) : null}

      {live ? (
        <button
          type="button"
          className="tb-now"
          title="pomakni časovnico na trenutno uro"
          onClick={() => pingNow()}
        >
          <span className="tb-now-dot" />
          zdaj {m2t(now)}
        </button>
      ) : null}

      {/* Only a plan write can be silent. */}
      {can('plan:write') ? (
        <button
          type="button"
          className={`tb-silent${silent ? ' tb-silent--on' : ''}`}
          title="Ko je vklopljeno, spremembe ne resetirajo potrditve prostovoljca — uporabi, ko se je o spremembi že dogovoril."
          onClick={() => setSilentOverride(!silent)}
        >
          <span className="tb-silent-box">{silent ? '✓' : ''}</span>
          popravek brez ponovne potrditve
        </button>
      ) : null}

      <div className="tb-right">
        {sectorFocus ? (
          <button type="button" className="tb-focus" onClick={() => setSectorFocus(null)}>
            {sectorFocus} ×
          </button>
        ) : null}
        {hoveredName ? (
          <button type="button" className="tb-trace" onClick={() => setHoveredVolunteer(null)}>
            <span className="tb-trace-tag">SLEDIM</span> {hoveredName} ×
          </button>
        ) : null}
      </div>
    </div>
  );
}
